import { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useTranslation } from 'react-i18next';

import { useAppTheme } from '@/theme/app-theme';

/**
 * Where Google returns the sign-in popup on web.
 *
 * This page does not complete the sign-in and must not try to. The handoff runs
 * at module scope in `src/app/_layout.tsx`, before any route renders, because
 * `maybeCompleteAuthSession` compares the CURRENT url against the redirect url
 * it stored and a route that had already moved the URL would fail that check.
 *
 * The route exists for two reasons. It gives the export a real
 * `/oauth/google/callback` page to emit, so Cloudflare Pages serves it rather
 * than relying on a fallback; and it gives `public/_headers` a path narrow
 * enough to strip `Cross-Origin-Opener-Policy` from without exempting the rest
 * of the app, which is what the whole fix turns on.
 *
 * On success the opener closes this window, so nothing below is ever seen. It
 * is the failure state that matters: deliberately NOT a redirect to `/welcome`,
 * because that is what the broken flow did — the popup quietly became a second
 * sign-in screen and the user opened another one, and another, with nothing
 * anywhere saying the handoff had failed.
 */
export default function GoogleOAuthCallback() {
  const { colors } = useAppTheme();
  const { t } = useTranslation();
  const [stalled, setStalled] = useState(false);

  useEffect(() => {
    // Comfortably longer than a working handoff, which resolves as soon as the
    // opener's message listener fires.
    const timeout = setTimeout(
      () => setStalled(true),
      6000,
    );

    return () => clearTimeout(timeout);
  }, []);

  return (
    <View
      style={[
        styles.screen,
        { backgroundColor: colors.background },
      ]}>
      {stalled ? (
        <Text
          style={[
            styles.text,
            { color: colors.textSecondary },
          ]}>
          {t('errors.oauthHandoffFailed')}
        </Text>
      ) : (
        <>
          <ActivityIndicator
            color={colors.primary}
          />
          <Text
            style={[
              styles.text,
              { color: colors.textSecondary },
            ]}>
            {t('auth.welcome.finishingGoogle')}
          </Text>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    paddingHorizontal: 32,
  },
  text: {
    fontSize: 15,
    fontWeight: '600',
    textAlign: 'center',
  },
});
